'use client';

import type { BoxProps, ElementProps, TooltipProps as MantineTooltipProps } from '@mantine/core';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  inline,
  offset as floatingOffset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import clsx from 'clsx';
import type { CSSProperties, ReactElement, ReactNode, Ref } from 'react';
import {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { LockedProps } from '../types';
import classes from './tooltip.module.css';

/**
 * Provides a custom portal root for all Tooltip floating elements within the
 * subtree. Use this in Shadow DOM embeds to keep tooltip portals inside the
 * shadow root instead of document.body.
 *
 * @example
 * // In an embed mounted into a shadow root:
 * <FloatingPortalRootContext.Provider value={mountPointElement}>
 *   <App />
 * </FloatingPortalRootContext.Provider>
 */
export const FloatingPortalRootContext = createContext<HTMLElement | null | undefined>(undefined);

export type TooltipProps = Omit<
  MantineTooltipProps,
  // `keyof BoxProps` covers every Mantine style prop (spacing, sizing, color, typography,
  // position) in one entry, and `ElementProps<'div'>` every DOM prop, handler and the `div` ref —
  // both are what Mantine's own Tooltip spreads onto its tooltip element, and neither reaches
  // this wrapper's floating element. Omitting them by `keyof` off Mantine's own building blocks
  // keeps the list honest across bumps instead of drifting behind ~60 style props and ~270
  // attributes.
  | LockedProps
  | keyof BoxProps
  | Exclude<keyof ElementProps<'div'>, 'children'>
  | 'arrowPosition'
  | 'arrowRadius'
  | 'arrowSize'
  | 'autoContrast'
  | 'color'
  | 'defaultOpened'
  | 'floatingStrategy'
  | 'keepMounted'
  | 'middlewares'
  | 'multiline'
  | 'offset'
  | 'portalProps'
  | 'radius'
  | 'refProp'
  | 'target'
  | 'transitionProps'
  // Mantine's styles-API `variant`; it reaches neither the floating element nor a Mantine
  // component here, and anything left in the prop surface is forwarded to the trigger.
  | 'variant'
  | 'withArrow'
  | 'withinPortal'
  | 'zIndex'
> & {
  /** Space between the trigger and the tooltip, in px. @default 5 */
  offset?: number;
};

const ARROW_SIZE = 6;
const DEFAULT_EVENTS = { hover: true, focus: false, touch: false } as const;

function getChildRef(child: ReactElement): Ref<unknown> | undefined {
  // React 19: ref is a regular prop — do not read element.ref (removed / warns).
  return (child.props as { ref?: Ref<unknown> }).ref;
}

type TriggerElementProps = { className?: string; style?: CSSProperties };

const FORWARD_REF_SYMBOL = Symbol.for('react.forward_ref');
const MEMO_SYMBOL = Symbol.for('react.memo');

/**
 * Determines whether a React element can have a ref attached via `cloneElement`.
 * Plain function components silently drop `ref` (other props are forwarded normally,
 * but the component must opt in to spread them onto a DOM node). Floating UI needs a
 * DOM node as its reference element, so for anything that isn't a host element,
 * class component, or forwardRef/memo wrapping the same, fall back to wrapping the
 * trigger in a <span>. Unknown element types (e.g. `lazy`) intentionally fall through
 * to the wrapper path for the same reason.
 */
function canAcceptRef(type: unknown): boolean {
  if (typeof type === 'string') return true;
  if (typeof type === 'function') {
    return Boolean(
      (type as { prototype?: { isReactComponent?: unknown } }).prototype?.isReactComponent,
    );
  }
  if (typeof type === 'object' && type !== null) {
    const sentinel = (type as { $$typeof?: symbol }).$$typeof;
    if (sentinel === FORWARD_REF_SYMBOL) return true;
    if (sentinel === MEMO_SYMBOL) {
      return canAcceptRef((type as { type?: unknown }).type);
    }
  }
  return false;
}

function getCloneableTriggerChild(node: ReactNode): ReactElement<TriggerElementProps> | null {
  const items = Children.toArray(node).filter((v) => v != null);
  if (items.length !== 1) return null;
  const only = items[0];
  if (!isValidElement(only)) return null;
  if (only.type === Fragment) return null;
  if (!canAcceptRef(only.type)) return null;
  return only as ReactElement<TriggerElementProps>;
}

/**
 * Everything Tooltip's own props left over. Mantine's `Menu.Target` / `Popover.Target` toggle by
 * cloning their child with `onClick`, `data-expanded`, the `aria-haspopup/expanded/controls`
 * linkage and an `id`; when that child is a Tooltip those land here instead of on the real trigger,
 * so they have to be handed down or the trigger goes inert (EPD-1286 — the dead "Show or hide
 * columns" button on every SmartTable). They arrive through `cloneElement`, never through a call
 * site, which is why {@link TooltipProps} stays locked.
 *
 * `className` and `style` are dropped: `PopoverTarget` always injects a `className`, and passing it
 * on would reopen the per-call-site styling hole {@link LockedProps} exists to close.
 */
function getInjectedTriggerProps(rest: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(rest).filter(([key]) => key !== 'className' && key !== 'style'),
  );
}

/** Chains an injected handler with the trigger's own of the same name so both run. */
function composeHandlers(injected: unknown, own: unknown): unknown {
  if (typeof injected !== 'function' || typeof own !== 'function') return own ?? injected;
  return (...args: unknown[]) => {
    (injected as (...handlerArgs: unknown[]) => void)(...args);
    (own as (...handlerArgs: unknown[]) => void)(...args);
  };
}

/** The trigger's own props with the injected ones merged in — the trigger wins on collisions. */
function mergeTriggerProps(
  injected: Record<string, unknown>,
  own: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...injected };
  for (const [key, value] of Object.entries(own ?? {})) {
    // An explicit `undefined` on the trigger (`<ActionIcon id={maybeUndefined}>`) must not blank
    // an injected value — the dropdown's aria linkage points at the injected `id`.
    if (value === undefined && key in merged) continue;
    merged[key] = key.startsWith('on') ? composeHandlers(injected[key], value) : value;
  }
  return merged;
}

/**
 * ### Tooltip
 *
 * @description
 * Mantine's Tooltip minus per-call-site style overrides (see {@link LockedProps}). Displays
 * additional text information inline; the arrow is aligned to the trigger via Floating UI. If you
 * need an _interactive_ tooltip (eg: a tooltip with a "Learn more" link) use `Popover` instead.
 *
 * Unlike the other core-ui wrappers this one does not render Mantine's Tooltip — it is built on
 * `@floating-ui/react` directly, so that a shadow-DOM embed can redirect the portal (see
 * {@link FloatingPortalRootContext}) and so the trigger child itself becomes the reference element
 * instead of gaining a wrapper `<span>`. That is why the omitted props are omitted rather than
 * forwarded:
 *
 * - **Look** (`color`, `radius`, `multiline`, `withArrow`, `arrowSize`, `arrowRadius`, `zIndex`, and
 *   every spacing/sizing style prop such as `p`/`w`/`maw`): owned by `tooltip.module.css` — one
 *   tooltip skin, 320px max width, arrow always on. A new look changes the module, not a call site —
 *   and not the theme either, for the reason recorded at the top of `tooltip.module.css`.
 * - **Portal** (`withinPortal`, `portalProps`): the floating element is always portalled, into
 *   {@link FloatingPortalRootContext}'s root when one is provided. Opting out, or passing Mantine
 *   `Portal` props, would break the shadow-DOM embeds.
 * - **Floating internals** (`middlewares`, `floatingStrategy`, `transitionProps`, `keepMounted`,
 *   `defaultOpened`, `arrowPosition`, `autoContrast`, `target`, `refProp`): this wrapper owns its
 *   middleware stack, `autoUpdate` positioning and 100ms fade, and the trigger child is always the
 *   reference element. Use `opened` for controlled visibility.
 * - **DOM props** (`id`, `role`, `aria-*`, `onClick` and friends): they belong on the trigger child,
 *   which — or whose fallback `<span>` — receives the merged reference props; `role="tooltip"` on the
 *   floating element is set by Floating UI's `useRole`. A parent that injects them through
 *   `cloneElement` rather than through a call site (Mantine's `Menu.Target` and `Popover.Target` do)
 *   is honoured all the same — see {@link getInjectedTriggerProps}.
 *
 * @example
 * <Tooltip label="Tooltip content">Tooltip target</Tooltip>
 *
 * @param props - {@link TooltipProps}
 */
const Tooltip = forwardRef<HTMLSpanElement, TooltipProps>((props, forwardedRef) => {
  const portalRoot = useContext(FloatingPortalRootContext);

  const {
    children,
    label,
    position = 'top',
    disabled = false,
    opened,
    offset: offsetPixels = 5,
    arrowOffset = 5,
    events: eventsProp,
    onPositionChange,
    openDelay = 0,
    closeDelay = 0,
    inline: useInlineMiddleware = false,
    ...rest
  } = props;

  const injectedTriggerProps = getInjectedTriggerProps(rest as Record<string, unknown>);

  const events = eventsProp ?? DEFAULT_EVENTS;
  const isControlled = typeof opened === 'boolean';
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = disabled ? false : isControlled ? opened : uncontrolledOpen;

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next);
      }
    },
    [isControlled],
  );

  const arrowRef = useRef<SVGSVGElement>(null);
  const previousPlacementRef = useRef<string | undefined>(undefined);

  const triggerChild = useMemo(() => getCloneableTriggerChild(children), [children]);
  const triggerChildRef = triggerChild ? getChildRef(triggerChild) : undefined;

  const middlewareOffset = offsetPixels + ARROW_SIZE / 2;

  const middleware = useMemo(
    () => [
      floatingOffset(middlewareOffset),
      ...(useInlineMiddleware ? [inline()] : []),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowRef, padding: arrowOffset }),
    ],
    [arrowOffset, middlewareOffset, useInlineMiddleware],
  );

  const { refs, floatingStyles, context, update, placement } = useFloating({
    placement: position,
    open,
    onOpenChange,
    middleware,
    whileElementsMounted: autoUpdate,
  });

  const mergedRef = useMergeRefs([refs.setReference, forwardedRef, triggerChildRef]);

  const hover = useHover(context, {
    enabled: !disabled && events.hover,
    move: true,
    delay: { open: openDelay, close: closeDelay },
    mouseOnly: !events.touch,
  });

  const focus = useFocus(context, {
    enabled: !disabled && events.focus,
    visibleOnly: true,
  });

  const dismiss = useDismiss(context, {
    enabled: !disabled && !isControlled,
  });

  const role = useRole(context, { role: 'tooltip', enabled: !disabled });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 100,
    initial: { opacity: 0 },
    open: { opacity: 1 },
    close: { opacity: 0 },
  });

  useEffect(() => {
    update();
  }, [update]);

  useEffect(() => {
    if (!onPositionChange) return;
    if (previousPlacementRef.current === undefined) {
      previousPlacementRef.current = placement;
      return;
    }
    if (previousPlacementRef.current === placement) return;
    previousPlacementRef.current = placement;
    onPositionChange(placement);
  }, [onPositionChange, placement]);

  // A label-less Tooltip on its own renders nothing and adds nothing: hand the children back.
  if (!label && Object.keys(injectedTriggerProps).length === 0) {
    return <>{children}</>;
  }

  // Disabled, or label-less: no floating element, but the trigger still has to receive the
  // injected props — on the trigger itself, so the `aria-expanded`/`aria-haspopup` state lands on
  // the focusable control rather than on a wrapper — plus the ref the parent positions against.
  if (!label || disabled) {
    return triggerChild ? (
      cloneElement(triggerChild as ReactElement<Record<string, unknown>>, {
        ...mergeTriggerProps(injectedTriggerProps, triggerChild.props as Record<string, unknown>),
        ref: mergedRef,
        className: clsx(classes.reference, triggerChild.props.className),
      })
    ) : (
      // Block-level, for the reason recorded on `.referenceFallback` in tooltip.module.css.
      <span ref={mergedRef} className={classes.referenceFallback} {...injectedTriggerProps}>
        {children}
      </span>
    );
  }

  // Floating UI's `mergeProps` chains every `on*` key it is handed, so passing the trigger's own
  // props here composes its handlers with the hover/focus/dismiss ones instead of overwriting them.
  const referenceProps = getReferenceProps(
    mergeTriggerProps(injectedTriggerProps, triggerChild?.props as Record<string, unknown>),
  ) as Record<string, unknown> & { className?: string; style?: CSSProperties };
  const { className: refClassName, style: refStyle, ...restReferenceProps } = referenceProps;

  const trigger = triggerChild ? (
    cloneElement(triggerChild as ReactElement<Record<string, unknown>>, {
      ...restReferenceProps,
      ref: mergedRef,
      className: clsx(classes.reference, triggerChild.props.className, refClassName),
      style: {
        ...(typeof triggerChild.props.style === 'object' && triggerChild.props.style !== null
          ? triggerChild.props.style
          : {}),
        ...refStyle,
      },
    })
  ) : (
    <span
      ref={mergedRef}
      className={clsx(classes.referenceFallback, refClassName)}
      {...restReferenceProps}
      style={refStyle}
    >
      {children}
    </span>
  );

  return (
    <>
      {trigger}
      {isMounted && (
        <FloatingPortal root={portalRoot}>
          <div
            ref={refs.setFloating}
            className={classes.floating}
            style={{ ...floatingStyles, ...transitionStyles }}
            {...getFloatingProps()}
          >
            {label}
            <FloatingArrow
              ref={arrowRef}
              context={context}
              width={ARROW_SIZE * 2}
              height={ARROW_SIZE}
              tipRadius={1}
              className={classes.arrow}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
});

Tooltip.displayName = 'Tooltip';

export default Tooltip;
